import {useMutation} from '@tanstack/react-query';
import ChessRestClient from '@/Api/ChessRestClient';
import {getLocalStorage} from '@/Utils/CommonUtils';

interface RecordRenewalLinkClickParams {
  // The bot source tag from the URL (?source=), e.g. "caissa_wa_bot".
  source: string;
  // The bot conversation ref from the URL (?ref=), if present.
  ref?: string | null;
}

interface RecordRenewalLinkClickResponse {
  success: boolean;
}

/**
 * Records a CLICK on a bot-shared renewal link — the page-load half of the
 * renewal funnel (the bot records "shared", the snapshot records "renewed").
 * The backend resolves the order from the authed user and is idempotent per
 * (order_id, source, ref), so calling once per mount is enough. Fire-and-forget:
 * callers should ignore failures — this must never affect the page.
 */
export default function useRecordRenewalLinkClick() {
  return useMutation<
    RecordRenewalLinkClickResponse,
    Error,
    RecordRenewalLinkClickParams
  >(async params => {
    const token = await getLocalStorage('access_token');
    if (!token) {
      throw new Error('Authentication token not found');
    }
    const response = await ChessRestClient.post<RecordRenewalLinkClickResponse>(
      'subscription/v1/renewal-link-clicked/',
      {source: params.source, ref: params.ref ?? null},
      {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
      },
    );
    return response.data;
  });
}
